import { useTranslation } from 'react-i18next';
import { Accordion } from '../common';
import { useCalculationStore } from '../../stores/calculationStore';
import { useCurrency } from '../../lib/utils/currency';

export function NetProfitProjections() {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const profit = useCalculationStore((state) => state.profit);

  if (!profit) {
    return null;
  }

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getProfitColor = (amount: number): string => {
    if (amount > 0) return 'text-green-600 dark:text-green-400';
    if (amount < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-900 dark:text-gray-100';
  };

  return (
    <Accordion 
      title={
        <div className="flex flex-col items-center justify-center w-full text-center">
          <span className="text-sm mb-1">{t('results.profitCalculations')}</span>
          <span className={`text-xl font-bold whitespace-nowrap ${getProfitColor(profit.netProfit)}`}>
            {formatCurrency(profit.netProfit)} {symbol}
          </span>
        </div>
      }
      defaultOpen={true}
    >
      <div className="space-y-4">
        {/* Summary */}
        <div className="space-y-2">
          <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('results.grossRevenueLabel')}
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrency(profit.grossRevenue)} {symbol}
            </span>
          </div>

          <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('results.totalExpensesVATExcl')}
            </span>
            <span className="text-sm font-semibold text-red-600 dark:text-red-400">
              -{formatCurrency(profit.totalExpenses)} {symbol}
            </span>
          </div>

          {/* Net Profit - Highlighted */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg border-2 border-blue-200 dark:border-blue-800">
            <div className="flex justify-between items-center">
              <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                {t('results.netProfitLabel')}
              </span>
              <span className={`text-2xl font-bold ${getProfitColor(profit.netProfit)}`}>
                {formatCurrency(profit.netProfit)} {symbol}
              </span>
            </div>
          </div>
        </div>

        {/* Annual Projections */}
        <div className="-mx-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 px-4">
            {t('results.annualProjections')}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase whitespace-nowrap">
                    {t('results.cyclesPerYear')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase whitespace-nowrap">
                    {t('results.annualRevenue')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase whitespace-nowrap">
                    {t('results.monthlyAverage')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {/* 5 Cycles */}
                {profit.annualProjections.map((projection, index) => (
                  <tr 
                    key={projection.cyclesPerYear}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${index === 1 ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {projection.cyclesPerYear} {t('results.cycles')}
                    </td>
                    <td className={`px-4 py-3 text-sm font-semibold text-right whitespace-nowrap ${getProfitColor(projection.annualRevenue)}`}>
                      {formatCurrency(projection.annualRevenue)} {symbol}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right whitespace-nowrap">
                      {formatCurrency(projection.monthlyAverage)} {symbol}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md text-xs text-gray-600 dark:text-gray-400">
          <p>
            📊 {t('results.projectionsInfo')}
          </p>
        </div>
      </div>
    </Accordion>
  );
}
